import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { useUserStore } from "@/store/userStore";
import { getLanguageInfo } from "@/lib/languages";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import * as flags from "country-flag-icons/react/3x2";
import { ReactNode } from "react";

interface CreditWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requiredCredits: number;
  supportedLanguages: string[];
  currentLanguage: string;
  onProceed: (selectedLanguages: string[]) => void;
}

export function CreditWarningDialog({
  open,
  onOpenChange,
  requiredCredits,
  supportedLanguages,
  currentLanguage,
  onProceed,
}: CreditWarningDialogProps) {
  const { getRemainingCredits } = useUserStore();
  const remainingCredits = getRemainingCredits();
  const t = useTranslations("MenuPage");
  const tLang = useTranslations("LanguageNames");

  // Initialize with all languages selected except current language
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(
    supportedLanguages.filter((lang) => lang !== currentLanguage)
  );

  const handleLanguageToggle = (language: string) => {
    setSelectedLanguages((prev) => {
      if (prev.includes(language)) {
        return prev.filter((lang) => lang !== language);
      } else {
        return [...prev, language];
      }
    });
  };

  const selectedCount = selectedLanguages.length;
  const canProceed = selectedCount <= remainingCredits;

  // Helper function to get a flag component by country code
  const getFlagComponent = (countryCode: string): ReactNode => {
    const FlagComponent = flags[countryCode as keyof typeof flags];
    return FlagComponent ? (
      <FlagComponent className="mx-2 w-6 h-4 rounded shadow-sm" />
    ) : null;
  };

  // Helper function to get country code for a language
  const getCountryCode = (languageCode: string): string => {
    // Special cases for Mandarin variants
    if (
      languageCode.toLowerCase() === "chinese_simplified" ||
      languageCode.toLowerCase() === "simplified_chinese" ||
      languageCode.toLowerCase() === "chinese-simplified"
    ) {
      return "CN"; // China flag for Simplified Chinese
    }
    if (
      languageCode.toLowerCase() === "chinese_traditional" ||
      languageCode.toLowerCase() === "traditional_chinese" ||
      languageCode.toLowerCase() === "chinese-traditional"
    ) {
      return "TW"; // Taiwan flag for Traditional Chinese
    }

    const countryMap: Record<string, string> = {
      arabic: "SA",
      bhojpuri: "IN",
      cantonese: "HK",
      chinese: "CN", // Generic Chinese
      chinese_simplified: "CN",
      chinese_traditional: "TW",
      czech: "CZ",
      dutch: "NL",
      english: "GB",
      filipino: "PH",
      french: "FR",
      german: "DE",
      hebrew: "IL",
      hindi: "IN",
      indonesian: "ID",
      italian: "IT",
      japanese: "JP",
      korean: "KR",
      malay: "MY",
      mandarin: "CN", // Generic Mandarin
      mandarin_simplified: "CN",
      mandarin_traditional: "TW",
      persian: "IR",
      polish: "PL",
      portuguese: "PT",
      romanian: "RO",
      russian: "RU",
      simplified_chinese: "CN",
      slovenian: "SI",
      spanish: "ES",
      swedish: "SE",
      thai: "TH",
      traditional_chinese: "TW",
      turkish: "TR",
      vietnamese: "VN",
    };

    return countryMap[languageCode.toLowerCase()] || "US"; // Default to US flag
  };

  // Get localized language name
  const getLocalizedLanguageName = (code: string): string => {
    // Special cases for languages with variants
    if (code === "chinese-simplified" || code === "chinese_simplified") {
      return tLang("chinese-simplified");
    }
    if (code === "chinese-traditional" || code === "chinese_traditional") {
      return tLang("chinese-traditional");
    }

    // Try to get translation from the LanguageNames namespace
    try {
      return tLang(code.toLowerCase());
    } catch {
      // Fallback to the language name from getLanguageInfo if translation is missing
      return getLanguageInfo(code)?.label || code;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("translationCredits")}</DialogTitle>
          <DialogDescription>
            {t("insufficientCreditsMessage", {
              needed: requiredCredits,
              available: remainingCredits,
            })}{" "}
            {t("selectLanguages")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {supportedLanguages.map((language) => {
              if (language === currentLanguage) return null;
              const langInfo = getLanguageInfo(language);

              return (
                <div
                  key={language}
                  className={cn(
                    "flex items-center space-x-2 p-2 bg-muted/50 rounded-md",
                    !selectedLanguages.includes(language) &&
                      selectedLanguages.length >= remainingCredits &&
                      "opacity-50"
                  )}
                >
                  <Checkbox
                    id={language}
                    checked={selectedLanguages.includes(language)}
                    onCheckedChange={() => handleLanguageToggle(language)}
                    disabled={
                      !selectedLanguages.includes(language) &&
                      selectedLanguages.length >= remainingCredits
                    }
                  />
                  <div className="flex-shrink-0">
                    {getFlagComponent(getCountryCode(language))}
                  </div>
                  <div className="flex flex-col">
                    <Label
                      htmlFor={language}
                      className={cn(
                        "text-sm font-medium",
                        !selectedLanguages.includes(language) &&
                          selectedLanguages.length >= remainingCredits &&
                          "text-muted-foreground"
                      )}
                    >
                      {langInfo?.nativeName}
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      {getLocalizedLanguageName(language)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-sm text-muted-foreground mt-2">
            {t("creditsRemaining", {
              count: selectedCount,
              plural: selectedCount === 1 ? "" : "s",
            })}
          </div>

          {!canProceed && (
            <div className="text-sm text-destructive">
              {t("notEnoughCreditsMessage")}
            </div>
          )}
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("cancel")}
          </Button>
          {canProceed ? (
            <Button
              onClick={() => {
                onProceed(selectedLanguages);
                onOpenChange(false);
              }}
            >
              {t("translateToLanguages", { count: selectedCount })}
            </Button>
          ) : (
            <Button
              onClick={() => {
                window.location.href =
                  "/dashboard/settings/plan#purchase-credits";
              }}
            >
              {t("getMoreCredits")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
